import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, HTTP_CONFLICT } from "../../lib/api";
import { addDays, shiftSpan, todayISO } from "../../lib/format";
import { useWorkSettings } from "../../components/ShiftFields";

export function usePlanningForm(initialSite) {
  const settings = useWorkSettings();
  const defaultShift = settings?.default_shift;
  const [form, setForm] = useState({ siteId: initialSite || "", start: todayISO(), end: addDays(todayISO(), 4), shift: "" });
  useEffect(() => {
    if (defaultShift) {
      setForm((p) => (p.shift ? p : { ...p, shift: defaultShift }));
    }
  }, [defaultShift]);
  const setField = useCallback((k, v) => setForm((p) => {
    const next = { ...p, [k]: v };
    if (k === "start" && v > p.end) next.end = v;
    return next;
  }), []);
  const valid = Boolean(form.siteId && form.start && form.end && form.end >= form.start && shiftSpan(form.shift));
  return { form, setField, valid };
}

export function usePlanningData(form, valid) {
  const { siteId, start, end, shift } = form;
  const [avail, setAvail] = useState(null);
  const [existing, setExisting] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    if (!valid) return;
    setLoading(true);
    const params = { site_id: siteId, start_date: start, end_date: end };
    try {
      const [a, ex] = await Promise.all([api.get("/planning/availability", { params: { ...params, shift } }), api.get("/assignments", { params })]);
      setAvail(a.data);
      setExisting(ex.data.items);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [valid, siteId, start, end, shift]);
  useEffect(() => { load(); }, [load]);
  return { avail, existing, loading, load };
}

export function useSelection(resetKey) {
  const [sel, setSel] = useState({ worker: new Set(), vehicle: new Set() });
  useEffect(() => { setSel({ worker: new Set(), vehicle: new Set() }); }, [resetKey]);
  const toggle = useCallback((type) => (id) => setSel((p) => {
    const n = new Set(p[type]);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    return { ...p, [type]: n };
  }), []);
  const setAll = useCallback((type, ids) => setSel((p) => ({ ...p, [type]: new Set(ids) })), []);
  return { selW: sel.worker, selV: sel.vehicle, toggle, setAll };
}

export function useEstimate(avail, selW, selV, hours, days) {
  return useMemo(() => {
    if (!avail) return 0;
    const w = avail.workers.filter((x) => selW.has(x.id)).reduce((s, x) => s + x.hourly_company_cost * hours * days, 0);
    const v = avail.vehicles.filter((x) => selV.has(x.id)).reduce((s, x) => s + x.daily_cost * days, 0);
    return w + v;
  }, [avail, selW, selV, hours, days]);
}

export function useResourceList(avail, tab, filter) {
  const list = useMemo(() => {
    const key = tab === "worker" ? "workers" : "vehicles";
    const f = filter.toLowerCase();
    return (avail?.[key] || []).filter((r) => `${r.name} ${r.qualification || r.type}`.toLowerCase().includes(f));
  }, [avail, tab, filter]);
  const sorted = useMemo(() => [...list].sort((a, b) => Number(b.available) - Number(a.available)), [list]);
  return { list, sorted };
}

const idsOf = (items, type) => new Set(items.filter((i) => i.resource_type === type).map((i) => i.resource_id));

export function useAssign(form, onDone) {
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null);
  const assign = async (wids, vids) => {
    setBusy(true);
    try {
      const r = await api.post("/assignments", { site_id: Number(form.siteId), start_date: form.start, end_date: form.end,
        worker_ids: wids, vehicle_ids: vids, shift: form.shift });
      toast.success(`✓ Assegnazione effettuata: ${r.data.count} risorse assegnate al cantiere`);
      setConflict(null);
      onDone();
    } catch (e) {
      if (e.response?.status !== HTTP_CONFLICT) {
        toast.error(`✕ ${errMsg(e)}`);
        return;
      }
      const items = e.response.data.detail.conflicts;
      const badW = idsOf(items, "worker");
      const badV = idsOf(items, "vehicle");
      setConflict({ items, okW: wids.filter((x) => !badW.has(x)), okV: vids.filter((x) => !badV.has(x)) });
      toast.error("⚠ Conflitto di pianificazione: salvataggio bloccato");
    } finally {
      setBusy(false);
    }
  };
  return { busy, conflict, setConflict, assign };
}
